// src/pages/Partner/PartnerTeam.jsx
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  CheckCircle2,
  Layers3,
  Mail,
  Pencil,
  RefreshCw,
  Search,
  ShieldCheck,
  UserPlus,
  Users,
  XCircle,
} from "lucide-react";
import {
  Link,
  useOutletContext,
  useSearchParams,
} from "react-router-dom";
import AddTeamMemberModal from "../../components/partner/AddTeamMemberModal";
import EditTeamMemberModal from "../../components/partner/EditTeamMemberModal";
import ManageCohortStaffModal from "../../components/partner/ManageCohortStaffModal";
import PartnerMemberInvitationsPanel from "../../components/partner/PartnerMemberInvitationsPanel";
import { fetchPartnerCohorts } from "../../lib/partner/partnerCohorts";
import { fetchPartnerTeam } from "../../lib/partner/partnerTeam";

const ROLE_LABELS = {
  owner: "Owner",
  admin: "Administrator",
  cohort_manager:
    "Cohort manager",
  facilitator: "Facilitator",
  viewer: "Viewer",
};

function roleClasses(role) {
  switch (role) {
    case "owner":
      return "bg-purple-100 text-purple-800";

    case "admin":
      return "bg-blue-100 text-blue-800";

    case "cohort_manager":
      return "bg-orange-100 text-orange-800";

    case "facilitator":
      return "bg-teal-100 text-teal-800";

    default:
      return "bg-gray-100 text-gray-700";
  }
}

function statusClasses(status) {
  switch (status) {
    case "active":
      return "bg-green-100 text-green-800";

    case "suspended":
      return "bg-red-100 text-red-700";

    default:
      return "bg-gray-200 text-gray-700";
  }
}

export default function PartnerTeam() {
  const {
    access,
  } = useOutletContext();

  const [
    searchParams,
    setSearchParams,
  ] = useSearchParams();

  const selectedCohortId =
    searchParams.get("cohort") || "";

  const [team, setTeam] =
    useState([]);

  const [cohorts, setCohorts] =
    useState([]);

  const [search, setSearch] =
    useState("");

  const [
    invitationRefreshKey,
    setInvitationRefreshKey,
  ] = useState(0);

  const [
    roleFilter,
    setRoleFilter,
  ] = useState("all");

  const [
    statusFilter,
    setStatusFilter,
  ] = useState("all");

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState("");

  const [message, setMessage] =
    useState("");

  const [
    addModalOpen,
    setAddModalOpen,
  ] = useState(false);

  const [
    editingMember,
    setEditingMember,
  ] = useState(null);

  const [
    assigningMember,
    setAssigningMember,
  ] = useState(null);

  const canManageTeam = [
    "owner",
    "admin",
  ].includes(
    access?.partner_role
  );

  const loadData =
    useCallback(async () => {
      try {
        setLoading(true);
        setError("");

        const [
          teamRows,
          cohortRows,
        ] = await Promise.all([
          fetchPartnerTeam(
            access?.partner_id ||
              null
          ),

          fetchPartnerCohorts(),
        ]);

        setTeam(teamRows);
        setCohorts(cohortRows);
      } catch (loadError) {
        console.error(
          "Partner team loading failed:",
          loadError
        );

        setError(
          loadError?.message ||
            "The partner team could not be loaded."
        );
      } finally {
        setLoading(false);
      }
    }, [access?.partner_id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredTeam =
    useMemo(() => {
      const cleanSearch =
        search
          .trim()
          .toLowerCase();

      return team.filter(
        (member) => {
          const matchesSearch =
            !cleanSearch ||
            [
              member.full_name,
              member.email,
              ROLE_LABELS[
                member.role
              ],
            ].some((value) =>
              String(value || "")
                .toLowerCase()
                .includes(cleanSearch)
            );

          const matchesRole =
            roleFilter === "all" ||
            member.role ===
              roleFilter;

          const matchesStatus =
            statusFilter ===
              "all" ||
            member.status ===
              statusFilter;

          const matchesCohort =
            !selectedCohortId ||
            member.assigned_cohorts.some(
              (cohort) =>
                cohort.id ===
                selectedCohortId
            );

          return (
            matchesSearch &&
            matchesRole &&
            matchesStatus &&
            matchesCohort
          );
        }
      );
    }, [
      team,
      search,
      roleFilter,
      statusFilter,
      selectedCohortId,
    ]);

  const summary =
    useMemo(() => {
      return team.reduce(
        (totals, member) => {
          totals.total += 1;

          if (
            member.status ===
            "active"
          ) {
            totals.active += 1;
          } else {
            totals.restricted += 1;
          }

          if (
            member.role ===
              "owner" ||
            member.role ===
              "admin"
          ) {
            totals.administrators += 1;
          }

          return totals;
        },
        {
          total: 0,
          active: 0,
          restricted: 0,
          administrators: 0,
        }
      );
    }, [team]);

  function handleCohortChange(
    event
  ) {
    const nextCohortId =
      event.target.value;

    const nextParams =
      new URLSearchParams(
        searchParams
      );

    if (nextCohortId) {
      nextParams.set(
        "cohort",
        nextCohortId
      );
    } else {
      nextParams.delete(
        "cohort"
      );
    }

    setSearchParams(nextParams);
  }

  function mayEditMember(
    member
  ) {
    if (!canManageTeam) {
      return false;
    }

    if (
      member.is_current_user
    ) {
      return false;
    }

    if (
      access?.partner_role ===
        "admin" &&
      member.role === "owner"
    ) {
      return false;
    }

    return true;
  }

  function mayAssignCohorts(
    member
  ) {
    return (
      canManageTeam &&
      member.status ===
        "active"
    );
  }

  function handleMemberInvited(
    result
  ) {
    setMessage(
      `An invitation was sent to ${result.email}.`
    );

    setInvitationRefreshKey(
      (current) =>
        current + 1
    );
  }

  async function handleMemberUpdated({
    member,
  }) {
    setMessage(
      `${member.full_name}'s partner access was updated.`
    );

    await loadData();
  }

  async function handleAssignmentsUpdated({
    member,
  }) {
    setMessage(
      `${member.full_name}'s cohort assignments were updated.`
    );

    await loadData();
  }

  return (
    <>
      <header className="rounded-[28px] border border-[#EFEFEF] bg-white p-6 shadow-sm md:p-8">
        <div className="flex flex-col justify-between gap-5 lg:flex-row lg:items-start">
          <div className="max-w-3xl">
            <p className="text-xs font-black uppercase tracking-wider text-[#FF914D]">
              Partner · Team
            </p>

            <h1 className="mt-2 text-[34px] font-black tracking-[-0.03em] text-[#333333] md:text-[42px]">
              Team
            </h1>

            <p className="mt-3 text-sm leading-relaxed text-[#757575] md:text-base">
              Manage team access, roles and cohort responsibilities for{" "}
              {access.partner_name}.
            </p>
          </div>

          {canManageTeam && (
            <button
              type="button"
              onClick={() =>
                setAddModalOpen(true)
              }
              className="inline-flex items-center justify-center self-start rounded-full bg-[#FF914D] px-5 py-3 text-sm font-black text-white transition hover:bg-[#E9782F]"
            >
              <UserPlus className="mr-2 h-4 w-4" />
              Invite team member
              </button>
          )}
        </div>
      </header>

      <section className="mt-7">
        <div className="mb-4">
          <p className="text-xs font-black uppercase tracking-wider text-[#FF914D]">
            Access overview
          </p>

          <h2 className="mt-1 text-xl font-black text-[#333333]">
            Team access
          </h2>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <SummaryCard
            label="Members"
            value={summary.total}
            description="All partner team accounts"
            icon={Users}
          />

          <SummaryCard
            label="Active"
            value={summary.active}
            description="Current active access"
            icon={CheckCircle2}
          />

          <SummaryCard
            label="Owners + admins"
            value={summary.administrators}
            description="Organisation administrators"
            icon={ShieldCheck}
          />

          <SummaryCard
            label="Restricted"
            value={summary.restricted}
            description="Inactive or suspended"
            icon={XCircle}
          />
        </div>
      </section>

      <section className="mt-6 rounded-[24px] border border-[#EFEFEF] bg-white p-5 shadow-sm md:p-6">
        <div className="mb-5">
          <p className="text-xs font-black uppercase tracking-wider text-[#FF914D]">
            Find team members
          </p>

          <h2 className="mt-1 text-lg font-black text-[#333333]">
            Search and filter
          </h2>

          <p className="mt-1 text-sm text-[#757575]">
            Find team members by name, email, cohort, role or access status.
          </p>
        </div>

        <div className="grid gap-4 xl:grid-cols-[minmax(220px,1fr)_220px_200px_200px]">
          <div>
            <label
              htmlFor="team-search"
              className="block text-xs font-black uppercase tracking-wider text-[#757575]"
            >
              Search
            </label>

            <div className="relative mt-2">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#999999]" />

              <input
                id="team-search"
                value={search}
                onChange={(event) =>
                  setSearch(
                    event.target.value
                  )
                }
                placeholder="Search name, email, or role"
                className="w-full rounded-2xl border border-[#DDDDDD] py-3 pl-11 pr-4 text-sm outline-none transition focus:border-[#FF914D] focus:ring-4 focus:ring-[#FF914D]/15"
              />
            </div>
          </div>

          <div>
            <label
              htmlFor="team-cohort-filter"
              className="block text-xs font-black uppercase tracking-wider text-[#757575]"
            >
              Cohort
            </label>

            <select
              id="team-cohort-filter"
              value={
                selectedCohortId
              }
              onChange={
                handleCohortChange
              }
              className="mt-2 w-full rounded-2xl border border-[#DDDDDD] bg-white px-4 py-3 text-sm outline-none transition focus:border-[#FF914D] focus:ring-4 focus:ring-[#FF914D]/15"
            >
              <option value="">
                All cohorts
              </option>

              {cohorts.map(
                (cohort) => (
                  <option
                    key={cohort.id}
                    value={cohort.id}
                  >
                    {cohort.name}
                  </option>
                )
              )}
            </select>
          </div>

          <div>
            <label
              htmlFor="team-role-filter"
              className="block text-xs font-black uppercase tracking-wider text-[#757575]"
            >
              Role
            </label>

            <select
              id="team-role-filter"
              value={roleFilter}
              onChange={(event) =>
                setRoleFilter(
                  event.target.value
                )
              }
              className="mt-2 w-full rounded-2xl border border-[#DDDDDD] bg-white px-4 py-3 text-sm outline-none transition focus:border-[#FF914D] focus:ring-4 focus:ring-[#FF914D]/15"
            >
              <option value="all">
                All roles
              </option>

              {Object.entries(
                ROLE_LABELS
              ).map(
                ([
                  value,
                  label,
                ]) => (
                  <option
                    key={value}
                    value={value}
                  >
                    {label}
                  </option>
                )
              )}
            </select>
          </div>

          <div>
            <label
              htmlFor="team-status-filter"
              className="block text-xs font-black uppercase tracking-wider text-[#757575]"
            >
              Status
            </label>

            <select
              id="team-status-filter"
              value={statusFilter}
              onChange={(event) =>
                setStatusFilter(
                  event.target.value
                )
              }
              className="mt-2 w-full rounded-2xl border border-[#DDDDDD] bg-white px-4 py-3 text-sm outline-none transition focus:border-[#FF914D] focus:ring-4 focus:ring-[#FF914D]/15"
            >
              <option value="all">
                All statuses
              </option>

              <option value="active">
                Active
              </option>

              <option value="inactive">
                Inactive
              </option>

              <option value="suspended">
                Suspended
              </option>
            </select>
          </div>
        </div>
      </section>

      {message && (
        <div
          role="status"
          className="mt-5 rounded-2xl border border-green-200 bg-green-50 px-4 py-3 text-sm font-semibold text-green-800"
        >
          {message}
        </div>
      )}

      {error && (
        <div
          role="alert"
          className="mt-5 flex items-center justify-between gap-4 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm font-semibold text-red-700"
        >
          <span>{error}</span>

          <button
            type="button"
            onClick={loadData}
            className="inline-flex flex-none items-center rounded-full border border-red-300 px-3 py-2"
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Retry
          </button>
        </div>
      )}

      {canManageTeam && (
        <PartnerMemberInvitationsPanel
          partnerId={
            access.partner_id
          }
          canManage={
            canManageTeam
          }
          refreshKey={
            invitationRefreshKey
          }
          onChanged={() =>
            setInvitationRefreshKey(
              (current) =>
                current + 1
            )
          }
        />
      )}

      {loading ? (
        <div className="mt-6 rounded-[24px] border border-[#EFEFEF] bg-white p-8 text-center text-sm font-semibold text-[#757575]">
          Loading partner team…
        </div>
      ) : filteredTeam.length ===
        0 ? (
        <section className="mt-6 rounded-[28px] border border-dashed border-[#FFB98B] bg-white p-8 text-center shadow-sm">
          <Users className="mx-auto h-8 w-8 text-[#FF914D]" />

          <h2 className="mt-4 text-xl font-black text-[#333333]">
            No team members found
          </h2>

          <p className="mx-auto mt-2 max-w-lg text-sm leading-relaxed text-[#757575]">
            No team members match the current search and filters.
          </p>
        </section>
      ) : (
        <section className="mt-6 overflow-hidden rounded-[24px] border border-[#EFEFEF] bg-white shadow-sm">
          <div className="flex flex-col gap-2 border-b border-[#EFEFEF] px-5 py-5 sm:flex-row sm:items-center sm:justify-between md:px-6">
            <div>
              <p className="text-xs font-black uppercase tracking-wider text-[#FF914D]">
                Team members
              </p>

              <h2 className="mt-1 text-lg font-black text-[#333333]">
                Partner access
              </h2>

              <p className="mt-1 text-sm text-[#757575]">
                Review roles, access status and cohort responsibilities.
              </p>
            </div>

            <div className="rounded-full bg-[#FFF4EC] px-3 py-1.5 text-xs font-black text-[#E9782F]">
              {filteredTeam.length}{" "}
              {filteredTeam.length === 1
                ? "member"
                : "members"}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-[#EFEFEF]">
              <thead className="bg-[#FFF9F5]">
                <tr>
                  <TableHeading>
                    Team member
                  </TableHeading>

                  <TableHeading>
                    Role
                  </TableHeading>

                  <TableHeading>
                    Status
                  </TableHeading>

                  <TableHeading>
                    Cohort Access
                  </TableHeading>

                  <TableHeading align="right">
                    Actions
                  </TableHeading>
                </tr>
              </thead>

              <tbody className="divide-y divide-[#EFEFEF]">
                {filteredTeam.map(
                  (member) => (
                    <tr
                      key={
                        member.partner_member_id
                      }
                      className="transition hover:bg-[#FFFDFB]"
                    >
                      <td className="whitespace-nowrap px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#FFF4EC] font-black text-[#FF914D]">
                            {String(
                              member.full_name ||
                                member.email ||
                                "T"
                            )
                              .charAt(0)
                              .toUpperCase()}
                          </div>

                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-black text-[#333333]">
                                {
                                  member.full_name
                                }
                              </p>

                              {member.is_current_user && (
                                <span className="rounded-full bg-[#F1F1F1] px-2 py-0.5 text-[10px] font-black uppercase tracking-wider text-[#757575]">
                                  You
                                </span>
                              )}
                            </div>

                            <a
                              href={`mailto:${member.email}`}
                              className="mt-1 inline-flex items-center text-xs font-semibold text-[#757575] hover:text-[#FF914D]"
                            >
                              <Mail className="mr-1.5 h-3.5 w-3.5" />

                              {
                                member.email
                              }
                            </a>
                          </div>
                        </div>
                      </td>

                      <td className="whitespace-nowrap px-5 py-4">
                        <span
                          className={[
                            "inline-flex rounded-full px-3 py-1 text-xs font-black",
                            roleClasses(
                              member.role
                            ),
                          ].join(" ")}
                        >
                          {
                            ROLE_LABELS[
                              member.role
                            ] ||
                            member.role
                          }
                        </span>
                      </td>

                      <td className="whitespace-nowrap px-5 py-4">
                        <span
                          className={[
                            "inline-flex rounded-full px-3 py-1 text-xs font-black capitalize",
                            statusClasses(
                              member.status
                            ),
                          ].join(" ")}
                        >
                          {
                            member.status
                          }
                        </span>
                      </td>

                      <td className="min-w-[260px] px-5 py-4">
                        {member.assigned_cohorts.length >
                        0 ? (
                          <div className="flex flex-wrap gap-2">
                            {member.assigned_cohorts
                              .slice(0, 3)
                              .map(
                                (cohort) => (
                                  <Link
                                    key={
                                      cohort.id
                                    }
                                    to={`/partner/cohorts/${cohort.id}`}
                                    className="rounded-full bg-[#FFF4EC] px-3 py-1 text-xs font-black text-[#E9782F]"
                                  >
                                    {
                                      cohort.name
                                    }
                                  </Link>
                                )
                              )}

                            {member.assigned_cohorts.length >
                              3 && (
                              <span className="rounded-full bg-[#F1F1F1] px-3 py-1 text-xs font-black text-[#757575]">
                                +
                                {member.assigned_cohorts.length -
                                  3}{" "}
                                more
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-sm font-semibold text-[#999999]">
                            No cohort
                            assignments
                          </span>
                        )}
                      </td>

                      <td className="whitespace-nowrap px-5 py-4 text-right">
                        <div className="inline-flex flex-wrap justify-end gap-2">
                          {mayAssignCohorts(
                            member
                          ) && (
                            <button
                              type="button"
                              onClick={() =>
                                setAssigningMember(
                                  member
                                )
                              }
                              className="inline-flex items-center rounded-full border border-[#FF914D] px-3 py-2 text-xs font-black text-[#FF914D] transition hover:bg-[#FFF4EC]"
                            >
                              <Layers3 className="mr-1.5 h-3.5 w-3.5" />
                              Cohorts
                            </button>
                          )}

                          {mayEditMember(
                            member
                          ) && (
                            <button
                              type="button"
                              onClick={() =>
                                setEditingMember(
                                  member
                                )
                              }
                              className="inline-flex items-center rounded-full border border-[#DDDDDD] px-3 py-2 text-xs font-black text-[#555555] transition hover:bg-[#F7F7F7]"
                            >
                              <Pencil className="mr-1.5 h-3.5 w-3.5" />
                              Edit
                            </button>
                          )}

                          {!mayAssignCohorts(
                            member
                          ) &&
                            !mayEditMember(
                              member
                            ) && (
                              <span className="text-xs font-semibold text-[#999999]">
                                No actions
                              </span>
                            )}
                        </div>
                      </td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <AddTeamMemberModal
        open={addModalOpen}
        partnerId={
          access.partner_id
        }
        partnerName={
          access.partner_name
        }
        actorRole={
          access.partner_role
        }
        allowOwner={
          access.partner_role ===
          "owner"
        }
        onClose={() =>
          setAddModalOpen(false)
        }
        onInvited={
          handleMemberInvited
        }
      />

      <EditTeamMemberModal
        open={Boolean(
          editingMember
        )}
        member={editingMember}
        actorRole={
          access.partner_role
        }
        onClose={() =>
          setEditingMember(null)
        }
        onUpdated={
          handleMemberUpdated
        }
      />

      <ManageCohortStaffModal
        open={Boolean(
          assigningMember
        )}
        member={assigningMember}
        initialCohortId={
          selectedCohortId
        }
        onClose={() =>
          setAssigningMember(null)
        }
        onUpdated={
          handleAssignmentsUpdated
        }
      />
    </>
  );
}

function SummaryCard({
  label,
  value,
  description,
  icon: Icon,
}) {
  return (
    <article className="rounded-[24px] border border-[#EFEFEF] bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-xs font-black uppercase tracking-wider text-[#757575]">
            {label}
          </p>

          <p className="mt-3 text-3xl font-black tracking-[-0.03em] text-[#333333]">
            {value}
          </p>

          {description && (
            <p className="mt-1 text-sm leading-relaxed text-[#757575]">
              {description}
            </p>
          )}
        </div>

        <div className="flex h-11 w-11 flex-none items-center justify-center rounded-2xl bg-[#FFF4EC] text-[#FF914D]">
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </article>
  );
}

function TableHeading({
  children,
  align = "left",
}) {
  return (
    <th
      scope="col"
      className={[
        "whitespace-nowrap px-5 py-3 text-xs font-black uppercase tracking-wider text-[#757575]",
        align === "right"
          ? "text-right"
          : "text-left",
      ].join(" ")}
    >
      {children}
    </th>
  );
}